/**
 * Pearl Configuration API client.
 *
 * Fetches per-office feature flags from the configuration service so the
 * widget can show/hide product icons (Voice, Second Opinion, Insights,
 * Insurance, etc.) based on what the organization has enabled.
 *
 * Delegates HTTP to `@hellopearl/cl-api-configuration`; the per-call bearer
 * token is set via a request interceptor that reads a module-level holder.
 */

import {
  initConfigurationApi,
  getAllConfigParams,
  CONFIG_SELECT_FIELDS,
  type OfficeConfig,
} from '@hellopearl/cl-api-configuration';
import { createLogger } from '../ext-core/logger';

const DEFAULT_CONFIG_API_ORIGIN = 'https://configuration-prod.hellopearl.com';

function resolveConfigApiOrigin(): string {
  if (process.env.CONFIG_API_ORIGIN) {
    return process.env.CONFIG_API_ORIGIN;
  }
  const fromGlobal = (globalThis as { __CONFIG_API_ORIGIN__?: unknown })
    .__CONFIG_API_ORIGIN__;
  if (typeof fromGlobal === 'string' && fromGlobal) return fromGlobal;
  return DEFAULT_CONFIG_API_ORIGIN;
}

export const OFFICE_PAGE_SIZE = 800;

export type { OfficeConfig };

export interface FeatureFlags {
  sodActive: boolean;
  piActive: boolean;
  insuranceActive: boolean;
  voiceActive: boolean;
}

let currentToken: string | null = null;
let initialized = false;

function ensureInitialized(): void {
  if (initialized) return;
  initialized = true;
  initConfigurationApi({
    baseUrl: `${resolveConfigApiOrigin()}/api/v1`,
    interceptors: (api: {
      interceptors: {
        request: {
          use: (
            fn: (config: Record<string, unknown>) => Record<string, unknown>,
          ) => void;
        };
      };
    }) => {
      api.interceptors.request.use((config: Record<string, unknown>) => {
        if (currentToken) {
          const headers = (config.headers || {}) as Record<string, string>;
          headers.Authorization = `Bearer ${currentToken}`;
          config.headers = headers;
        }
        return config;
      });
    },
  });
}

export async function fetchConfiguration(
  accessToken: string,
  organizationId: string,
): Promise<OfficeConfig[]> {
  const cfgLogger = createLogger('configuration', 'content');
  cfgLogger.info('config_api_request', { organizationId });

  ensureInitialized();
  currentToken = accessToken;

  try {
    const result = await getAllConfigParams({
      organizationId,
      payload: { select: [...CONFIG_SELECT_FIELDS] },
      pageLimit: OFFICE_PAGE_SIZE,
    });
    const offices = (result.data ?? []) as OfficeConfig[];
    cfgLogger.info('config_api_loaded', { officeCount: offices.length });
    return offices;
  } finally {
    currentToken = null;
  }
}

export function deriveFeatureFlags(offices: OfficeConfig[]): FeatureFlags {
  return {
    sodActive: offices.some(o => o.sod_status === 'Active'),
    piActive: offices.some(o => o.pi_status === 'Active'),
    insuranceActive: offices.some(o => o.insurance_status === 'Active'),
    voiceActive: offices.some(o => o.voice_status === 'Active'),
  };
}

export async function fetchFeatureFlags(
  accessToken: string,
  decodeJwt: (token: string) => Record<string, unknown>,
  extractOrgId: (payload: Record<string, unknown>) => string | null,
): Promise<FeatureFlags | null> {
  const payload = decodeJwt(accessToken);
  const orgId = extractOrgId(payload);
  if (!orgId) return null;
  const offices = await fetchConfiguration(accessToken, orgId);
  return deriveFeatureFlags(offices);
}
